import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList,
  Loader2,
  X,
  MapPin,
  Play,
  CheckCircle2,
  Camera,
  PenLine,
  AlertTriangle,
  Phone,
  RefreshCw,
  Clock,
  ChevronLeft,
  ChevronRight,
  Save,
  Eye,
  Paperclip,
  FileText,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import {
  missionsApi,
  type Mission,
  type MissionStatus,
} from '../services/api';
import { useNotificationsStore } from '../store/notifications.store';
import PdfViewer from '../components/PdfViewer';

// i18n keys — resolve via t() at render time.
const STATUS_LABELS: Record<MissionStatus, string> = {
  DRAFT: 'my_missions.status_draft',
  SCHEDULED: 'my_missions.status_scheduled',
  IN_PROGRESS: 'my_missions.status_in_progress',
  COMPLETED: 'my_missions.status_completed',
  CANCELLED: 'my_missions.status_cancelled',
};

const STATUS_COLORS: Record<MissionStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 border-gray-200',
  SCHEDULED: 'bg-blue-100 text-blue-700 border-blue-200',
  IN_PROGRESS: 'bg-amber-100 text-amber-800 border-amber-300',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
};

function formatTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms: number | undefined | null): string {
  if (!ms || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
  if (m > 0) return `${m} min`;
  return `${totalSec} sec`;
}

export default function MyMissionsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [completingMission, setCompletingMission] = useState<Mission | null>(null);
  const [focusedMissionId, setFocusedMissionId] = useState<string | null>(null);
  const prevMissionsRef = useRef<Map<string, string>>(new Map()); // id → JSON fingerprint
  const initialLoadDoneRef = useRef(false);
  // Currently displayed day. Defaults to today, navigable via the header arrows.
  // If ?date=YYYY-MM-DD is in the URL (e.g. coming from /my-tours), honor it.
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      const d = new Date(`${dateParam}T00:00:00`);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Pick up a focus target from the URL once on mount, then strip it so a
  // subsequent navigation doesn't keep highlighting the same row.
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus) {
      setFocusedMissionId(focus);
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once missions are loaded, scroll the focused one into view.
  useEffect(() => {
    if (!focusedMissionId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`mission-${focusedMissionId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(t);
  }, [focusedMissionId, missions]);

  // Attachment viewer
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [reportPdfUrl, setReportPdfUrl] = useState<string | null>(null);
  // Waze confirmation
  const [wazeUrl, setWazeUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const viewerTouchRef = useRef<{ scale: number; lastDist: number; startX: number; startY: number; translateX: number; translateY: number; isPinching: boolean }>({
    scale: 1, lastDist: 0, startX: 0, startY: 0, translateX: 0, translateY: 0, isPinching: false,
  });

  // Completion form state
  const [notes, setNotes] = useState('');
  const [signerName, setSignerName] = useState('');
  const [proofPhotos, setProofPhotos] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);

  // Format YYYY-MM-DD for the API query string (local time, not UTC)
  const formatDateParam = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const loadMissions = useCallback(async (dateOverride?: Date) => {
    try {
      const date = dateOverride ?? selectedDate;
      const data = await missionsApi.listMyToday(formatDateParam(date));

      // Detect new / modified missions (after initial load) and notify
      if (initialLoadDoneRef.current) {
        for (const m of data) {
          const fingerprint = `${m.title}|${m.address}|${m.scheduledAt}|${m.status}|${m.description}|${m.type}|${(m.attachmentUrls || []).length}`;
          const prev = prevMissionsRef.current.get(m.id);
          if (!prev) {
            // New mission
            useNotificationsStore.getState().addNotification({
              type: 'info',
              title: `Nouvelle mission : ${m.reference}`,
              message: m.title,
              data: { missionId: m.id },
            });
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`Nouvelle mission : ${m.reference}`, {
                body: m.title,
                icon: '/pwa-192x192.png',
                tag: `mission-new-${m.id}`,
              });
            }
          } else if (prev !== fingerprint) {
            // Modified mission
            useNotificationsStore.getState().addNotification({
              type: 'info',
              title: `Mission modifiée : ${m.reference}`,
              message: m.title,
              data: { missionId: m.id },
            });
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`Mission modifiée : ${m.reference}`, {
                body: m.title,
                icon: '/pwa-192x192.png',
                tag: `mission-mod-${m.id}`,
              });
            }
          }
        }
      }

      // Update fingerprints
      const newMap = new Map<string, string>();
      for (const m of data) {
        newMap.set(m.id, `${m.title}|${m.address}|${m.scheduledAt}|${m.status}|${m.description}|${m.type}|${(m.attachmentUrls || []).length}`);
      }
      prevMissionsRef.current = newMap;
      initialLoadDoneRef.current = true;

      setMissions(data);
      setError(null);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    // Reset notification tracking when date changes — the first load
    // of a new date is silent (no "new mission" spam).
    initialLoadDoneRef.current = false;
    prevMissionsRef.current = new Map();
    loadMissions();
    // Auto-refresh every 30s so the driver gets new assignments quickly.
    const interval = setInterval(() => loadMissions(), 30_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Convenience helpers for date navigation
  const shiftDate = (deltaDays: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + deltaDays);
    d.setHours(0, 0, 0, 0);
    setSelectedDate(d);
  };
  const goToToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setSelectedDate(d);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = selectedDate.getTime() === today.getTime();
  const isPast = selectedDate.getTime() < today.getTime();

  const handleStart = async (m: Mission) => {
    setBusy(m.id);
    try {
      await missionsApi.start(m.id);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur démarrage');
    } finally {
      setBusy(null);
    }
  };

  const openCompletion = (m: Mission) => {
    setCompletingMission(m);
    // Pre-fill with any draft data already saved on the mission so the driver
    // can resume where they left off (e.g. they took a "before" photo earlier
    // and now want to add the "after" photo and signature).
    setNotes(m.completionNotes ?? '');
    setSignerName(m.signerName ?? '');
    setProofPhotos(m.proofPhotoUrls ?? []);
  };

  // Signature canvas init
  useEffect(() => {
    if (!completingMission || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1f2937';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [completingMission]);

  const getCanvasPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = 'touches' in e ? e.touches[0]!.clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0]!.clientY : (e as React.MouseEvent).clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    drawingRef.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getCanvasPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    drawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      for (const file of Array.from(files)) {
        const result = await missionsApi.uploadProof(file);
        setProofPhotos((prev) => [...prev, result.url]);
      }
    } catch {
      alert('Erreur upload photo');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /**
   * Reads the signature canvas and returns the uploaded URL if there is ink,
   * otherwise falls back to the existing signature so it isn't dropped on draft save.
   */
  const captureSignature = async (): Promise<string | null> => {
    if (!canvasRef.current) return completingMission?.signatureUrl ?? null;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let hasInk = false;
    for (let i = 0; i < imageData.data.length; i += 4) {
      if (imageData.data[i]! < 250 || imageData.data[i + 1]! < 250 || imageData.data[i + 2]! < 250) {
        hasInk = true;
        break;
      }
    }
    if (hasInk) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );
      if (blob) {
        const file = new File([blob], 'signature.png', { type: 'image/png' });
        const result = await missionsApi.uploadProof(file);
        return result.url;
      }
    }
    // Fallback: keep the existing signature if any (e.g. it was set in a previous draft save)
    return completingMission?.signatureUrl ?? null;
  };

  const handleSaveDraft = async () => {
    if (!completingMission) return;
    setBusy(completingMission.id);
    try {
      const signatureUrl = await captureSignature();
      await missionsApi.saveDraft(completingMission.id, {
        notes: notes || null,
        signerName: signerName || null,
        signatureUrl,
        proofPhotoUrls: proofPhotos,
      });
      setCompletingMission(null);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur d\'enregistrement du brouillon');
    } finally {
      setBusy(null);
    }
  };

  const handleComplete = async () => {
    if (!completingMission) return;
    if (!confirm('Finaliser la mission ? Une fois validée, plus aucune modification ne sera possible.')) {
      return;
    }
    setBusy(completingMission.id);
    try {
      const signatureUrl = await captureSignature();
      await missionsApi.complete(completingMission.id, {
        notes: notes || null,
        signerName: signerName || null,
        signatureUrl,
        proofPhotoUrls: proofPhotos,
      });
      setCompletingMission(null);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur complétion');
    } finally {
      setBusy(null);
    }
  };

  const handleViewPdf = async (m: Mission) => {
    setBusy(m.id);
    try {
      const blob = await missionsApi.downloadPdf(m.id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setReportPdfUrl(url);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur d\'ouverture du PDF');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-safe">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary-600" /> {t('my_missions.title')}
          </h1>
          <button
            onClick={() => loadMissions()}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full"
            title={t('my_missions.refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Date navigator */}
        <div className="border-t border-gray-100 px-2 py-2 flex items-center justify-between gap-2">
          <button
            onClick={() => shiftDate(-1)}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-full active:scale-95 transition-transform"
            title={t('my_missions.prev_day')}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 flex flex-col items-center min-w-0">
            <p
              className={`text-sm font-semibold capitalize truncate ${
                isToday ? 'text-primary-700' : isPast ? 'text-gray-500' : 'text-gray-900'
              }`}
            >
              {isToday
                ? t('my_missions.today')
                : selectedDate.toLocaleDateString('fr-FR', { weekday: 'long' })}
            </p>
            <p
              className={`text-xs ${
                isToday ? 'text-primary-600' : isPast ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              {selectedDate.toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>

          <button
            onClick={() => shiftDate(1)}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-full active:scale-95 transition-transform"
            title={t('my_missions.next_day')}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* "Aujourd'hui" shortcut when not on today */}
        {!isToday && (
          <div className="px-4 pb-2 -mt-1">
            <button
              onClick={goToToday}
              className="w-full py-1.5 text-xs text-primary-600 hover:bg-primary-50 rounded-lg font-medium border border-primary-200"
            >
              {t('my_missions.back_to_today')}
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Missions list */}
      {missions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <ClipboardList className="w-16 h-16 text-gray-300 mb-3" />
          <p className="text-gray-500 mb-1 font-medium">
            {isToday
              ? t('my_missions.no_today')
              : isPast
                ? t('my_missions.no_past')
                : t('my_missions.no_future')}
          </p>
          <p className="text-xs text-gray-400 max-w-xs">
            {isToday ? t('my_missions.no_today_hint') : t('my_missions.navigate_hint')}
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {missions.map((m) => {
            const isBusy = busy === m.id;
            return (
              <div
                key={m.id}
                id={`mission-${m.id}`}
                className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden transition-all ${
                  focusedMissionId === m.id
                    ? 'border-emerald-400 ring-2 ring-emerald-200'
                    : m.status === 'IN_PROGRESS'
                    ? 'border-amber-300 ring-2 ring-amber-100'
                    : 'border-gray-200'
                }`}
              >
                {/* Card header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                    <span
                      className={`text-[10px] font-bold px-2 py-1 rounded-full border uppercase tracking-wide ${STATUS_COLORS[m.status]}`}
                    >
                      {t(STATUS_LABELS[m.status])}
                    </span>
                    <span className="text-xs font-mono text-gray-400">{m.reference}</span>
                  </div>
                  <h2 className="text-base font-semibold text-gray-900 mb-1">{m.title}</h2>
                  {m.scheduledAt && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {t('my_missions.scheduled_at', { time: formatTime(m.scheduledAt) })}
                      {m.estimatedDurationMin && (
                        <span className="text-gray-400"> · {t('my_missions.duration_estimate', { min: m.estimatedDurationMin })}</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Card body */}
                <div className="p-4 space-y-2">
                  {/* Address + Waze button */}
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700">{m.address}</p>
                      {(m.postalCode || m.city) && (
                        <p className="text-xs text-gray-500">
                          {m.postalCode} {m.city}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setWazeUrl(
                        `https://waze.com/ul?q=${encodeURIComponent(
                          `${m.address} ${m.postalCode ?? ''} ${m.city ?? ''}`,
                        )}&navigate=yes`,
                      )
                    }
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#33ccff]/10 border border-[#33ccff]/30 text-[#0099cc] rounded-lg text-sm font-semibold active:scale-[0.97] transition-transform"
                  >
                    <MapPin className="w-4 h-4" />
                    {t('my_missions.waze_route')}
                  </button>

                  {/* Client */}
                  {m.clientName && (
                    <div className="text-sm text-gray-700 flex items-center gap-2">
                      <span className="w-4 shrink-0 text-gray-400">👤</span>
                      <span className="font-medium">{m.clientName}</span>
                      {m.clientPhone && (
                        <a
                          href={`tel:${m.clientPhone}`}
                          className="ml-auto p-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-full"
                          title={t('my_missions.call_client', { phone: m.clientPhone })}
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  {m.description && (
                    <p className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 rounded p-2">
                      {m.description}
                    </p>
                  )}

                  {/* Attachments */}
                  {m.attachmentUrls && m.attachmentUrls.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                        <Paperclip className="w-3 h-3" /> {t('my_missions.attachments_count', { count: m.attachmentUrls.length })}
                      </p>
                      {/* Image thumbnails */}
                      {m.attachmentUrls.some((u) => /\.(jpe?g|png|webp)$/i.test(u)) && (
                        <div className="flex gap-2 overflow-x-auto pb-2 mb-1.5">
                          {m.attachmentUrls
                            .filter((u) => /\.(jpe?g|png|webp)$/i.test(u))
                            .map((url) => (
                              <button
                                key={url}
                                type="button"
                                onClick={() => setViewerUrl(url)}
                                className="shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 hover:ring-2 hover:ring-primary-400 transition-all"
                              >
                                <img
                                  src={url}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              </button>
                            ))}
                        </div>
                      )}
                      {/* PDF files */}
                      {m.attachmentUrls
                        .filter((u) => /\.pdf$/i.test(u))
                        .map((url) => {
                          const parts = url.split('/');
                          const filename = parts[parts.length - 1] || '';
                          // UUID is 36 chars, followed by '-', then original filename
                          const displayName = filename.length > 37 ? filename.substring(37) : filename;
                          return (
                            <button
                              key={url}
                              type="button"
                              onClick={() => setViewerUrl(url)}
                              className="w-full flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs hover:bg-red-100 transition-colors mb-1 text-left"
                            >
                              <FileText className="w-4 h-4 text-red-500 shrink-0" />
                              <span className="text-gray-700 truncate flex-1 font-medium">{displayName}</span>
                              <Eye className="w-3 h-3 text-gray-400 shrink-0" />
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Action button */}
                <div className="p-3 border-t border-gray-100 bg-gray-50">
                  {m.status === 'SCHEDULED' || m.status === 'DRAFT' ? (
                    <button
                      onClick={() => handleStart(m)}
                      disabled={isBusy}
                      className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white rounded-lg font-semibold text-base flex items-center justify-center gap-2 shadow-sm active:scale-[0.98] transition-transform"
                    >
                      {isBusy ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Play className="w-5 h-5" />
                      )}
                      {t('my_missions.start_intervention')}
                    </button>
                  ) : m.status === 'IN_PROGRESS' ? (
                    (() => {
                      const hasDraft =
                        !!m.completionNotes ||
                        !!m.signatureUrl ||
                        (m.proofPhotoUrls && m.proofPhotoUrls.length > 0);
                      return (
                        <button
                          onClick={() => openCompletion(m)}
                          className="w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold text-base flex items-center justify-center gap-2 shadow-sm active:scale-[0.98] transition-transform"
                        >
                          <CheckCircle2 className="w-5 h-5" />
                          {hasDraft ? t('my_missions.resume_finalize') : t('my_missions.finish')}
                        </button>
                      );
                    })()
                  ) : m.status === 'COMPLETED' ? (
                    <div className="space-y-2">
                      <div className="w-full py-2 bg-green-50 text-green-700 rounded-lg text-sm flex items-center justify-center gap-2 font-medium">
                        <CheckCircle2 className="w-4 h-4" /> {t('my_missions.completed_at', { time: formatTime(m.completedAt) })}
                      </div>
                      {m.interventionDurationMs > 0 && (
                        <div className="w-full py-1.5 bg-white border border-gray-200 rounded-lg text-xs flex items-center justify-center gap-1.5 text-gray-700">
                          <Clock className="w-3.5 h-3.5 text-gray-500" />
                          {t('my_missions.intervention_duration')} <strong>{formatDuration(m.interventionDurationMs)}</strong>
                        </div>
                      )}
                      <button
                        onClick={() => handleViewPdf(m)}
                        disabled={isBusy}
                        className="w-full py-3 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 text-gray-800 rounded-lg text-sm flex items-center justify-center gap-2 font-medium active:scale-[0.98] transition-transform"
                      >
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        {t('my_missions.view_pdf')}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Completion fullscreen modal */}
      {completingMission && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div
            className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10"
            style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
          >
            <button onClick={() => setCompletingMission(null)} className="p-2 -ml-2 text-gray-600">
              <X className="w-6 h-6" />
            </button>
            <h2 className="font-semibold text-gray-900">{t('my_missions.complete_modal_title')}</h2>
            <div className="w-10" />
          </div>

          <div className="p-4 space-y-5">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
              <p className="text-xs font-mono text-primary-600 mb-0.5">{completingMission.reference}</p>
              <p className="font-semibold text-gray-900">{completingMission.title}</p>
              <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {completingMission.address}
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('my_missions.notes_label')}</label>
              <textarea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('my_missions.notes_placeholder')}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
              />
            </div>

            {/* Photos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('my_missions.photos_label')}</label>
              {proofPhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {proofPhotos.map((url, i) => (
                    <div key={i} className="relative aspect-square">
                      <img src={url} alt="" className="w-full h-full object-cover rounded-lg border border-gray-200" />
                      <button
                        onClick={() => setProofPhotos((prev) => prev.filter((u) => u !== url))}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 active:bg-gray-50 flex items-center justify-center gap-2"
              >
                <Camera className="w-5 h-5" /> {t('my_missions.take_or_pick')}
              </button>
            </div>

            {/* Signer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('my_missions.signer_label')}</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder={t('my_missions.signer_placeholder')}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
              />
            </div>

            {/* Signature */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <PenLine className="w-4 h-4" /> {t('my_missions.signature')}
                </label>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-xs text-gray-500 underline"
                >
                  {t('my_missions.clear')}
                </button>
              </div>
              <canvas
                ref={canvasRef}
                width={600}
                height={220}
                className="w-full border-2 border-gray-300 rounded-lg bg-white touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              <p className="text-[10px] text-gray-500 mt-1">{t('my_missions.sign_hint')}</p>
            </div>
          </div>

          {/* Sticky footer — two buttons: save draft + finalize */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 pb-safe space-y-2">
            {/* Timer explanation — visible above the action buttons so the
                driver understands how the duration counter accumulates */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <p className="text-[11px] text-blue-900 leading-snug">
                <Clock className="inline w-3 h-3 mr-1 -mt-0.5" />
                <strong>{t('my_missions.timer_intro')}</strong> {t('my_missions.timer_body')}
              </p>
            </div>
            <button
              onClick={handleSaveDraft}
              disabled={busy === completingMission.id}
              className="w-full py-3 bg-amber-100 hover:bg-amber-200 disabled:bg-gray-100 text-amber-800 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
            >
              {busy === completingMission.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('my_missions.save_draft')}
            </button>
            <button
              onClick={handleComplete}
              disabled={busy === completingMission.id}
              className="w-full py-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-lg font-semibold text-base flex items-center justify-center gap-2 shadow-sm"
            >
              {busy === completingMission.id ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-5 h-5" />
              )}
              {t('my_missions.finalize')}
            </button>
            <p className="text-[10px] text-gray-500 text-center px-2">
              {t('my_missions.draft_hint')}
            </p>
          </div>
        </div>
      )}

      {/* Waze confirmation modal */}
      {wazeUrl && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-6" onClick={() => setWazeUrl(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-[#33ccff]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-[#0099cc]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('my_missions.waze_open_title')}</h3>
              <p className="text-sm text-gray-500">
                {t('my_missions.waze_open_body')}
              </p>
            </div>
            <div className="flex border-t border-gray-200">
              <button
                onClick={() => setWazeUrl(null)}
                className="flex-1 py-3.5 text-sm font-semibold text-gray-600 active:bg-gray-50 border-r border-gray-200"
              >
                {t('my_missions.cancel')}
              </button>
              <a
                href={wazeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setWazeUrl(null)}
                className="flex-1 py-3.5 text-sm font-bold text-[#0099cc] active:bg-[#33ccff]/5 text-center"
              >
                {t('my_missions.open_waze')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* PDF viewer (attachments) */}
      {viewerUrl && /\.pdf$/i.test(viewerUrl) && (
        <PdfViewer url={viewerUrl} onClose={() => setViewerUrl(null)} />
      )}

      {/* PDF viewer (mission report) */}
      {reportPdfUrl && (
        <PdfViewer url={reportPdfUrl} onClose={() => { URL.revokeObjectURL(reportPdfUrl); setReportPdfUrl(null); }} />
      )}

      {/* Image viewer (fullscreen overlay with pinch-to-zoom) */}
      {viewerUrl && !(/\.pdf$/i.test(viewerUrl)) && (
        <div
          className="fixed inset-0 z-[100] bg-black flex flex-col"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 shrink-0">
            <button
              onClick={() => {
                setViewerUrl(null);
                viewerTouchRef.current = { scale: 1, lastDist: 0, startX: 0, startY: 0, translateX: 0, translateY: 0, isPinching: false };
                if (imgRef.current) imgRef.current.style.transform = 'translate(0px, 0px) scale(1)';
              }}
              className="flex items-center gap-2 text-white text-sm font-medium active:opacity-70"
            >
              <ChevronLeft className="w-5 h-5" />
              Retour
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  const t = viewerTouchRef.current;
                  const img = imgRef.current;
                  if (!img) return;
                  t.scale = Math.max(1, t.scale - 0.5);
                  if (t.scale <= 1) { t.translateX = 0; t.translateY = 0; }
                  img.style.transform = `translate(${t.translateX}px, ${t.translateY}px) scale(${t.scale})`;
                }}
                className="w-10 h-10 flex items-center justify-center text-white bg-white/20 rounded-full active:bg-white/40"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  const t = viewerTouchRef.current;
                  const img = imgRef.current;
                  if (!img) return;
                  t.scale = Math.min(5, t.scale + 0.5);
                  img.style.transform = `translate(${t.translateX}px, ${t.translateY}px) scale(${t.scale})`;
                }}
                className="w-10 h-10 flex items-center justify-center text-white bg-white/20 rounded-full active:bg-white/40"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div
            className="flex-1 overflow-hidden flex items-center justify-center"
            onTouchStart={(e) => {
              const t = viewerTouchRef.current;
              if (e.touches.length === 2) {
                t.isPinching = true;
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                t.lastDist = Math.sqrt(dx * dx + dy * dy);
              } else if (e.touches.length === 1 && t.scale > 1) {
                t.startX = e.touches[0].clientX - t.translateX;
                t.startY = e.touches[0].clientY - t.translateY;
              }
            }}
            onTouchMove={(e) => {
              const t = viewerTouchRef.current;
              const img = imgRef.current;
              if (!img) return;
              if (e.touches.length === 2 && t.isPinching) {
                e.preventDefault();
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const delta = dist / t.lastDist;
                t.scale = Math.min(5, Math.max(1, t.scale * delta));
                t.lastDist = dist;
                if (t.scale <= 1) { t.translateX = 0; t.translateY = 0; }
                img.style.transform = `translate(${t.translateX}px, ${t.translateY}px) scale(${t.scale})`;
              } else if (e.touches.length === 1 && t.scale > 1 && !t.isPinching) {
                t.translateX = e.touches[0].clientX - t.startX;
                t.translateY = e.touches[0].clientY - t.startY;
                img.style.transform = `translate(${t.translateX}px, ${t.translateY}px) scale(${t.scale})`;
              }
            }}
            onTouchEnd={(e) => {
              const t = viewerTouchRef.current;
              const img = imgRef.current;
              if (e.touches.length < 2) t.isPinching = false;
              if (t.scale <= 1 && img) {
                t.scale = 1; t.translateX = 0; t.translateY = 0;
                img.style.transform = 'translate(0px, 0px) scale(1)';
              }
            }}
          >
            <img
              ref={imgRef}
              src={viewerUrl}
              alt=""
              className="max-w-full max-h-full object-contain"
              style={{ transformOrigin: 'center center', willChange: 'transform' }}
              draggable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
